import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  CloudUpload,
  Download,
  FileSpreadsheet,
  LayoutGrid,
  List as ListIcon,
  SlidersHorizontal,
  LoaderCircle,
  MessageSquareText,
  Search,
  SearchX,
  Upload,
  Wand2,
  X
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  apiClient,
  type SubmittedUpload,
  type UploadSummary,
  type UploadTemplateInfo
} from "@/shared/api/api-client";
import { usePolling } from "@/shared/hooks/usePolling";
import { formatDuration, uploadTiming } from "./upload-metrics";
import {
  ACTIVE_STATUSES,
  canDownload,
  downloadBlob,
  progressPercent,
  statusMeta,
  uploadIssues,
  uploadLabel
} from "./upload-shared";
import { useNavigate } from "react-router-dom";
import { AppLink } from "@/shared/components/AppLink";
import { DateField } from "@/shared/components/DateField";
import { ToolbarSelect } from "@/shared/components/ToolbarSelect";
import { formatInteger } from "@/shared/utils/format";
import { periodoDaConsulta } from "@/shared/utils/instant-period";
import {
  UPLOAD_SITUATIONS,
  UPLOAD_SITUATION_LABELS,
  type UploadSituation
} from "@/shared/vocabulary/upload-situations";

import { ApiError, errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { ExportFeedback, useExportRequest } from "@/features/exports/use-export-request";
import type { ImportableDimension } from "@/shared/vocabulary/canonical-fields";
import { ImportedClassificationDialog } from "./ImportedClassificationDialog";
import {
  availableDimensions,
  describeImported,
  hasImportedChoice,
  pruneDimensions
} from "./imported-dimensions";
import { Select } from "@/shared/components/Select";
import { Pager } from "@/shared/components/Pager";

/** Quantos uploads por página. O seletor fica sempre no rodapé, então o
 *  rodapé não some mais com uma página só (2026-10-19). */
const UPLOAD_PAGE_SIZES = [10, 25, 50] as const;
const DEFAULT_PAGE_SIZE = 10;

/** Lista (tabela) ou cards — escolha da pessoa, lembrada no navegador como
 *  o modo de leitura da tela de menções. Conveniência, não estado: sem
 *  storage (aba privada) a tela abre em lista. */
type UploadsView = "list" | "cards";
const VIEW_KEY = "reputaition.uploads_view";
const PAGE_SIZE_KEY = "reputaition.uploads_page_size";

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage bloqueado: a escolha vale só nesta visita.
  }
}

type UploadFilters = {
  situation: UploadSituation | "";
  templateId: number | null;
  /** Dias "AAAA-MM-DD"; viram instantes com fuso na consulta, porque o
   *  envio é carimbo de verdade (ver `periodoDaConsulta`). */
  from: string;
  to: string;
};

const NO_FILTERS: UploadFilters = { situation: "", templateId: null, from: "", to: "" };

/**
 * Envio + grade de cards, um card por upload. O detalhe (métricas,
 * linhas sem menção, erros, fila de correção) mora em página própria —
 * clicar no card navega pra ela. Era um acordeão inline aqui, até
 * acumular quatro seções e parar de caber.
 */
type UploadsPageProps = {
  token: string;
  /** Destino do detalhe de um upload. Link, e não callback (2026-10-26):
   * "Abrir detalhes" vale clique do meio e "abrir em nova aba". */
  uploadHref: (uploadId: number) => string;
  /** Destino das menções de um upload (ver `uploadMentionsHref`). */
  mentionsHref: (uploadId: number) => string;
  /** Leva à página de Downloads, onde a planilha pedida aparece. */
  onOpenDownloads?: () => void;
};

const MAX_FILES = 10;

type PendingFile = {
  file: File;
  inspecting: boolean;
  rowCount: number | null;
  /** Ids do template — o detectado e o escolhido. Id, e não nome, desde
   *  2026-10-04: o nome é renomeável, e o que a tela guardou ao soltar o
   *  arquivo deixava de existir se alguém renomeasse antes do envio. */
  detected: number | null;
  template: number | null;
  refreshEngagement: boolean;
  /** As dimensões que a pessoa mandou tomar DA PLANILHA ("Configurações
   *  avançadas"). Vazio é o padrão: tudo pelo Reputaition. */
  importedDimensions: ImportableDimension[];
  error: string | null;
};

export function UploadsPage({
  token,
  uploadHref,
  mentionsHref,
  onOpenDownloads
}: UploadsPageProps) {
  // O clique na LINHA ou no CARD inteiro também abre o detalhe (os
  // links dentro deles param a propagação, então não há clique duplo).
  const navigate = useNavigate();
  // A planilha do upload vai pra fila (2026-10-15). O hook acompanha o
  // último pedido; `requestedFor` diz de qual card é, pra só ele girar.
  const exporter = useExportRequest({ token, onOpenDownloads });
  const [requestedFor, setRequestedFor] = useState<number | null>(null);
  const [templates, setTemplates] = useState<UploadTemplateInfo[]>([]);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [drag, setDrag] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResults, setSubmitResults] = useState<SubmittedUpload[] | null>(null);
  const [uploads, setUploads] = useState<UploadSummary[]>([]);
  const [total, setTotal] = useState(0);
  // A primeira resposta da lista já chegou (com dado ou com erro). Antes
  // dela a lista vazia NÃO é "nenhum upload ainda" — é "ainda não sei",
  // e na rede da FSB isso dura segundos (feedback da Kath, 2026-10-26:
  // "quando entra, não aparece nada, só alguns segundos depois").
  const [loaded, setLoaded] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  // Falha do ENVIO como um todo (429, rede, 422 do lote), separada do
  // erro de CADA arquivo (`item.error`, que é "este arquivo é ilegível"
  // e o tira do envio). Até 2026-10-09 o `catch` gravava a mensagem em
  // todos os itens: um 429 deixava "Enviar 0 arquivos" desabilitado, e
  // a pessoa precisava remover e soltar tudo de novo pra tentar outra vez.
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Falha de AÇÃO sobre a lista (baixar o modelo em branco), separada do
  // `listError`: não troca a lista pelo aviso de carregamento.
  const [actionError, setActionError] = useState<string | null>(null);
  // Até quando o polling da lista fica em silêncio depois de um 429 com
  // `Retry-After` — a mesma pausa que o detalhe do upload faz.
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  // A lista é paginada (2026-10-16): eram os 20 últimos de uma vez, e a tela
  // crescia sem parar.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState<number>(() => {
    const stored = Number(readStored(PAGE_SIZE_KEY));
    return (UPLOAD_PAGE_SIZES as readonly number[]).includes(stored) ? stored : DEFAULT_PAGE_SIZE;
  });
  const [view, setViewState] = useState<UploadsView>(() =>
    readStored(VIEW_KEY) === "cards" ? "cards" : "list"
  );
  const [filters, setFilters] = useState<UploadFilters>(NO_FILTERS);
  // A busca anda em duas: o que está na caixa e o que foi pra consulta,
  // 350 ms depois da última tecla — senão cada letra seria uma requisição.
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const filtering =
    filters.situation !== "" ||
    filters.templateId !== null ||
    filters.from !== "" ||
    filters.to !== "" ||
    appliedQuery.trim() !== "";
  const invertedPeriod = filters.from !== "" && filters.to !== "" && filters.from > filters.to;

  const setView = (next: UploadsView) => {
    setViewState(next);
    writeStored(VIEW_KEY, next);
  };
  const setPageSize = (next: number) => {
    setPageSizeState(next);
    writeStored(PAGE_SIZE_KEY, String(next));
    setPage(1);
  };
  const changeFilters = (next: Partial<UploadFilters>) => {
    setFilters(current => ({ ...current, ...next }));
    setPage(1);
  };
  const clearFilters = () => {
    setFilters(NO_FILTERS);
    setQuery("");
    setAppliedQuery("");
    setPage(1);
  };

  useEffect(() => {
    if (query === appliedQuery) return;
    const timer = window.setTimeout(() => {
      setAppliedQuery(query);
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [query, appliedQuery]);
  const [advancedFor, setAdvancedFor] = useState<PendingFile | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // O modelo em branco vem do servidor em xlsx (2026-10-17): o CSV que a
  // tela montava abria numa célula só no Excel PT-BR.
  const downloadTemplate = useCallback(
    async (template: UploadTemplateInfo) => {
      try {
        const blob = await apiClient.downloadUploadTemplateSpreadsheet(token, template.id);
        downloadBlob(blob, `modelo-${template.name}.xlsx`);
        setActionError(null);
      } catch (error) {
        setActionError(errorMessage(error, "Não foi possível baixar o modelo."));
      }
    },
    [token]
  );

  useEffect(() => {
    apiClient.uploadTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  // Descarte por CARGA: com filtro mudando rápido, a resposta do recorte
  // anterior pode chegar depois da do atual e trocaria a lista por ela.
  const loadSeq = useRef(0);
  const reloadList = useCallback(async () => {
    const seq = ++loadSeq.current;
    const period = periodoDaConsulta(filters.from, filters.to);
    try {
      const result = await apiClient.listUploads(token, {
        page,
        size: pageSize,
        ...(filters.situation ? { situation: filters.situation } : {}),
        ...(filters.templateId !== null ? { uploadTemplateId: filters.templateId } : {}),
        ...(appliedQuery.trim() ? { q: appliedQuery.trim() } : {}),
        ...(period.changed_from ? { uploadedFrom: period.changed_from } : {}),
        ...(period.changed_to ? { uploadedTo: period.changed_to } : {})
      });
      if (seq !== loadSeq.current) return;
      setLoaded(true);
      // Página que ficou além do total (filtro estreitou, ou o último da
      // página sumiu): volta pra última válida em vez de mostrar vazio.
      const lastPage = Math.max(1, Math.ceil(result.total / pageSize));
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setUploads(result.items);
      setTotal(result.total);
      setListError(null);
      setPausedUntil(null);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      setLoaded(true);
      if (error instanceof ApiError && error.retryAfterSeconds !== null) {
        // Sem a pausa o polling seguia a cada 2,5 s contra uma cerca
        // que pediu 30 s, e cada batida era outro 429.
        setPausedUntil(Date.now() + error.retryAfterSeconds * 1000);
      }
      setListError(errorMessage(error, "Não foi possível carregar os uploads."));
    }
  }, [token, page, pageSize, filters, appliedQuery]);

  useEffect(() => {
    void reloadList();
  }, [reloadList]);

  // Polling enquanto a esteira trabalha — é assim que a barra anda, e
  // é o mesmo re-render que faz a duração ao vivo andar.
  const hasActive = uploads.some(upload => ACTIVE_STATUSES.has(upload.status));
  const now = Date.now();
  // Assinatura do backoff: progresso e status de cada upload. Enquanto
  // um arquivo espera na fila nada disso muda, e o intervalo sobe.
  const progressSignature = uploads
    .map(upload => `${upload.id}:${upload.status}:${upload.processed_rows}`)
    .join(",");
  usePolling({
    enabled: hasActive,
    onTick: () => void reloadList(),
    signature: progressSignature,
    pausedUntil
  });

  const addFiles = (incoming: File[]) => {
    const accepted = incoming.filter(file => /\.(csv|xlsx|xls)$/i.test(file.name));
    setSubmitResults(null);
    setPending(current => {
      const room = MAX_FILES - current.length;
      const batch = accepted.slice(0, room).map<PendingFile>(file => ({
        file,
        inspecting: true,
        rowCount: null,
        detected: null,
        template: null,
        refreshEngagement: false,
        importedDimensions: [],
        error: null
      }));
      // Inspect de cada arquivo em paralelo — o servidor detecta o
      // template pela estrutura, o select nasce preenchido.
      for (const item of batch) {
        apiClient
          .inspectUpload(token, item.file)
          .then(result => {
            setPending(files =>
              files.map(f =>
                f.file === item.file
                  ? {
                      ...f,
                      inspecting: false,
                      rowCount: result.row_count,
                      detected: result.detected_upload_template_id,
                      template: result.detected_upload_template_id,
                      importedDimensions: pruneDimensions(
                        f.importedDimensions,
                        availableDimensions(templates.find(t => t.id === result.detected_upload_template_id))
                      )
                    }
                  : f
              )
            );
          })
          .catch(error => {
            setPending(files =>
              files.map(f =>
                f.file === item.file
                  ? { ...f, inspecting: false, error: errorMessage(error, "Arquivo ilegível.") }
                  : f
              )
            );
          });
      }
      return [...current, ...batch];
    });
  };

  const submit = async () => {
    const ready = pending.filter(item => !item.inspecting && !item.error);
    if (!ready.length) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await apiClient.submitUploads(
        token,
        ready.map(item => ({
          file: item.file,
          uploadTemplateId: item.template,
          refreshEngagementMetrics: item.refreshEngagement,
          // Defesa: o template pode ter sido trocado depois da escolha.
          importedDimensions: pruneDimensions(
            item.importedDimensions,
            availableDimensions(templates.find(t => t.id === item.template))
          )
        }))
      );
      setSubmitResults(result.uploads);
      setPending([]);
      // O upload novo entra no topo da página 1: é lá que a pessoa quer vê-lo.
      // Um filtro ativo poderia escondê-lo ("Concluídos" não mostra o que
      // acabou de entrar na fila), então o envio limpa o recorte.
      if (filtering || page !== 1) clearFilters();
      else await reloadList();
    } catch (error) {
      // Os arquivos continuam prontos: a falha é do ENVIO, não deles.
      // O `displayMessage` de um 422 já traz o campo e o índice do
      // arquivo que o servidor recusou.
      setSubmitResults(null);
      setSubmitError(errorMessage(error, "Falha no envio."));
    } finally {
      setSubmitting(false);
    }
  };

  const requestSpreadsheet = (upload: UploadSummary) => {
    setRequestedFor(upload.id);
    exporter.clearError();
    void exporter.request({
      kind: "export_upload_rows",
      subject_id: upload.id,
      format: "xlsx"
    });
  };

  const readyCount = pending.filter(item => !item.inspecting && !item.error).length;

  const itemActions = (upload: UploadSummary) => ({
    downloading: exporter.busy && requestedFor === upload.id,
    onOpen: () => navigate(uploadHref(upload.id)),
    uploadHref: uploadHref(upload.id),
    mentionsHref: mentionsHref(upload.id),
    onDownload: () => requestSpreadsheet(upload)
  });

  return (
    <div className="uploads-page">
      {/* ── Envio ── */}
      <section className="card upload-step">
        <div className="upload-step-head">
          <div>
            <h3>Enviar planilhas</h3>
            <p>Até {MAX_FILES} arquivos por envio — o modelo da clipadora é detectado pela estrutura de cada um.</p>
          </div>
        </div>
        <div
          className={`upload-zone ${drag ? "dragging" : ""}`}
          onDragOver={event => { event.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={event => {
            event.preventDefault();
            setDrag(false);
            addFiles(Array.from(event.dataTransfer.files));
          }}
        >
          <div className="upload-icon"><CloudUpload size={22}/></div>
          <h3>Arraste as planilhas aqui</h3>
          <p>
            ou <button onClick={() => inputRef.current?.click()}>escolha os arquivos</button>
          </p>
          <span>CSV, XLSX ou XLS</span>
          <input
            ref={inputRef}
            type="file"
            hidden
            multiple
            accept=".csv,.xlsx,.xls"
            onChange={event => {
              addFiles(Array.from(event.target.files ?? []));
              event.target.value = "";
            }}
          />
        </div>

        {pending.length > 0 && (
          <div className="pending-files">
            {pending.map((item, index) => (
              <div key={`${item.file.name}-${index}`} className="pending-file-row">
                <FileSpreadsheet size={17}/>
                <div className="pending-file-copy">
                  <strong>{item.file.name}</strong>
                  <span>
                    {item.inspecting
                      ? "Lendo estrutura…"
                      : item.error
                        ? item.error
                        : `${item.rowCount} linhas · ${item.detected ? "modelo detectado" : "modelo não reconhecido — escolha abaixo"}`}
                  </span>
                </div>
                <Select
                  aria-label={`Modelo de ${item.file.name}`}
                  value={item.template === null ? "" : String(item.template)}
                  disabled={item.inspecting || Boolean(item.error)}
                  onChange={event => {
                    const next = event.target.value ? Number(event.target.value) : null;
                    // Trocar o modelo derruba a escolha de classificação que o
                    // novo modelo não mapeia — senão o envio mandaria uma
                    // dimensão que o servidor recusa.
                    const available = availableDimensions(templates.find(t => t.id === next));
                    setPending(files =>
                      files.map(f =>
                        f === item
                          ? { ...f, template: next, importedDimensions: pruneDimensions(f.importedDimensions, available) }
                          : f
                      )
                    );
                  }}
                >
                  <option value="">Escolher modelo…</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </Select>
                <label className="pending-file-refresh" title="Atualizar métricas de engajamento de posts repetidos">
                  <input
                    type="checkbox"
                    checked={item.refreshEngagement}
                    onChange={event =>
                      setPending(files =>
                        files.map(f => (f === item ? { ...f, refreshEngagement: event.target.checked } : f))
                      )
                    }
                  />
                  <span>Atualizar métricas</span>
                </label>
                {availableDimensions(templates.find(t => t.id === item.template)).length > 0 && (
                  <button
                    type="button"
                    className="text-button pending-file-advanced"
                    aria-label={`Configurações avançadas de ${item.file.name}`}
                    title={
                      hasImportedChoice(item.importedDimensions)
                        ? `Classificação da planilha: ${describeImported(item.importedDimensions)}`
                        : "Tudo classificado pelo Reputaition"
                    }
                    disabled={item.inspecting || Boolean(item.error)}
                    onClick={() => setAdvancedFor(item)}
                  >
                    <SlidersHorizontal size={14} aria-hidden="true" />
                    Configurações avançadas
                    {hasImportedChoice(item.importedDimensions) && (
                      <span
                        className="advanced-attention-dot"
                        role="img"
                        aria-label="Há configurações fora do padrão"
                      />
                    )}
                  </button>
                )}
                <button
                  className="icon-button"
                  aria-label={`Remover ${item.file.name}`}
                  onClick={() => setPending(files => files.filter(f => f !== item))}
                >
                  <X size={15}/>
                </button>
              </div>
            ))}
            {submitError && (
              <ApiState kind="error">{submitError}</ApiState>
            )}
            <div className="upload-card-foot">
              <button className="primary-button" disabled={submitting || readyCount === 0} onClick={() => void submit()}>
                {submitting ? <LoaderCircle className="spin" size={16}/> : <Upload size={16}/>}
                Enviar {readyCount} {readyCount === 1 ? "arquivo" : "arquivos"}
              </button>
            </div>
          </div>
        )}

        {advancedFor && (
          <ImportedClassificationDialog
            fileName={advancedFor.file.name}
            available={availableDimensions(templates.find(t => t.id === advancedFor.template))}
            selected={pending.find(f => f.file === advancedFor.file)?.importedDimensions ?? []}
            onChange={next =>
              setPending(files =>
                files.map(f => (f.file === advancedFor.file ? { ...f, importedDimensions: next } : f))
              )
            }
            onClose={() => setAdvancedFor(null)}
          />
        )}

        {submitResults && (
          <div className="submit-results">
            {submitResults.map(result => (
              <div
                key={result.upload_id}
                className={`upload-feedback ${result.status === "UPLOADED" ? "success" : "error"}`}
              >
                {result.status === "UPLOADED" ? <CheckCircle2 size={15}/> : <AlertTriangle size={15}/>}
                <strong>{result.filename}</strong>
                {result.status === "UPLOADED"
                  ? ` — ${result.total_rows} linhas na fila de processamento.`
                  : ` — ${result.message ?? "modelo inválido"}${result.missing_columns.length ? ` (faltam: ${result.missing_columns.join(", ")})` : ""}`}
              </div>
            ))}
          </div>
        )}

        {templates.length > 0 && (
          <p className="upload-template-link">
            <Wand2 size={14}/> Precisa de um modelo em branco?{" "}
            {templates.map((template, index) => (
              <span key={template.name}>
                {index > 0 && " · "}
                <button
                  className="link-button"
                  onClick={() => void downloadTemplate(template)}
                >
                  {template.name}
                </button>
              </span>
            ))}
          </p>
        )}
      </section>

      {/* ── Uploads: lista ou cards ── */}
      <section className="card upload-history-card">
        <div className="card-title uploads-list-head">
          <h3>
            <FileSpreadsheet size={17}/>
            Uploads · {formatInteger(total)}
            {filtering && <small className="uploads-list-filtered"> no filtro</small>}
          </h3>
          <ToolbarSelect<UploadsView>
            prefix="Ver"
            ariaLabel="Como ver os uploads"
            align="right"
            value={view}
            onChange={setView}
            options={[
              { value: "list", label: "Lista", icon: ListIcon },
              { value: "cards", label: "Cards", icon: LayoutGrid }
            ]}
          />
        </div>

        {/* Os filtros falam a língua das outras telas de empresa (a barra do
            Histórico de edições): caixa de busca, seletor em pílula e o
            calendário da plataforma. Todos recortam no SERVIDOR, e o total
            do título e do rodapé é o do recorte. */}
        <div className="uploads-filters">
          <div className="search-field">
            <Search size={17} aria-hidden="true" />
            <input
              name="uploads-search"
              autoComplete="off"
              aria-label="Buscar pelo nome do arquivo"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Buscar pelo nome do arquivo…"
            />
          </div>
          <ToolbarSelect<UploadSituation | "">
            prefix="Situação"
            ariaLabel="Situação"
            value={filters.situation}
            options={[
              { value: "", label: "Todas" },
              ...UPLOAD_SITUATIONS.map(situation => ({
                value: situation,
                label: UPLOAD_SITUATION_LABELS[situation]
              }))
            ]}
            onChange={situation => changeFilters({ situation })}
          />
          {templates.length > 0 && (
            <ToolbarSelect<string>
              prefix="Clipadora"
              ariaLabel="Clipadora"
              value={filters.templateId === null ? "" : String(filters.templateId)}
              options={[
                { value: "", label: "Todas" },
                ...templates.map(template => ({ value: String(template.id), label: template.name }))
              ]}
              onChange={value => changeFilters({ templateId: value ? Number(value) : null })}
            />
          )}
          <div className="edit-history-period">
            <span className="toolbar-select-label">Enviados de</span>
            <DateField
              ariaLabel="Enviados de"
              value={filters.from}
              max={filters.to || undefined}
              onValue={from => changeFilters({ from })}
            />
            <span className="toolbar-select-label">até</span>
            <DateField
              ariaLabel="Enviados até"
              value={filters.to}
              min={filters.from || undefined}
              align="right"
              onValue={to => changeFilters({ to })}
            />
          </div>
          {filtering && (
            <button type="button" className="ghost-button" onClick={clearFilters}>
              <SlidersHorizontal size={14} aria-hidden="true" />
              Limpar filtros
            </button>
          )}
        </div>
        {invertedPeriod && (
          <p className="form-error" role="alert">
            A data inicial é depois da final — nenhum upload cabe nesse período.
          </p>
        )}

        {listError && (
          <ApiState kind="error" onRetry={() => void reloadList()}>
            {listError}
          </ApiState>
        )}
        {/* Falha de AÇÃO sobre a lista (pedir a planilha), separada da
            falha de CARREGAMENTO: a segunda troca a lista pelo aviso com
            "tentar de novo"; a primeira só avisa, com a lista no lugar
            (regra de 2026-09-10, a mesma do detalhe do upload). */}
        <ExportFeedback exporter={exporter} />
        {actionError && <ApiState kind="error">{actionError}</ApiState>}
        {!loaded && !listError && (
          <ApiState kind="loading">Carregando os uploads…</ApiState>
        )}
        {loaded && !listError && uploads.length === 0 && (
          filtering ? (
            <div className="upload-history-empty">
              <SearchX size={26}/>
              <strong>Nenhum upload neste filtro</strong>
              <button type="button" className="link-button" onClick={clearFilters}>
                Limpar filtros
              </button>
            </div>
          ) : (
            <div className="upload-history-empty">
              <FileSpreadsheet size={26}/>
              <strong>Nenhum upload ainda</strong>
              <span>Solte a primeira planilha acima pra ver a esteira trabalhar.</span>
            </div>
          )
        )}

        {/* Um `now` só pro lote inteiro, lido no render. O polling da lista
            já re-renderiza a cada 2,5s enquanto algo processa, então a
            duração ao vivo anda sozinha. */}
        {uploads.length > 0 && view === "list" && (
          // A lista é uma TABELA enxuta (2026-10-16): uma linha por upload.
          // A linha inteira abre o detalhe pelo mouse; pelo teclado, o botão
          // "Abrir detalhes" faz o mesmo (`<tr>` não recebe foco).
          <div className="uploads-table-wrap">
            <table className="ai-admin-table uploads-table">
              <thead>
                <tr>
                  <th scope="col">Arquivo</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="is-number">Linhas</th>
                  <th scope="col" className="is-number">Tempo</th>
                  <th scope="col">Menções</th>
                  <th scope="col"><span className="sr-only">Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {uploads.map(upload => (
                  <UploadRow key={upload.id} upload={upload} now={now} {...itemActions(upload)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {uploads.length > 0 && view === "cards" && (
          <div className="upload-card-grid">
            {uploads.map(upload => (
              <UploadCard key={upload.id} upload={upload} now={now} {...itemActions(upload)} />
            ))}
          </div>
        )}
        <Pager
          page={page}
          size={pageSize}
          count={{ total }}
          onPage={setPage}
          sizes={UPLOAD_PAGE_SIZES}
          onSize={setPageSize}
        />
      </section>
    </div>
  );
}

type UploadItemProps = {
  upload: UploadSummary;
  now: number;
  downloading: boolean;
  onOpen: () => void;
  uploadHref: string;
  mentionsHref: string;
  onDownload: () => void;
};

/** O que ficou pra trás, numa linha só (ver `uploadIssues`). */
function UploadIssuesLine({ upload }: { upload: UploadSummary }) {
  const { issues, title } = uploadIssues(upload);
  if (issues.length === 0) return null;
  return (
    <span className="upload-issues" title={title}>
      {issues.map((issue, index) => (
        <span key={issue.text} className={`upload-issue ${issue.tone}`}>
          {index > 0 && <span className="upload-issue-sep" aria-hidden="true">·</span>}
          {issue.tone === "warning" && <AlertTriangle size={13} aria-hidden="true" />}
          {issue.text}
        </span>
      ))}
    </span>
  );
}

function MentionsLink({ upload, mentionsHref }: { upload: UploadSummary; mentionsHref: string }) {
  if (upload.mention_count === 0) {
    return <span className="upload-card-no-mentions">Nenhuma menção gerada</span>;
  }
  return (
    <AppLink className="link-button" to={mentionsHref} stopPropagation>
      <MessageSquareText size={13}/>
      Ver {formatInteger(upload.mention_count)} {upload.mention_count === 1 ? "menção" : "menções"}
    </AppLink>
  );
}

function DownloadButton({ upload, downloading, onDownload }: Pick<UploadItemProps, "upload" | "downloading" | "onDownload">) {
  if (!canDownload(upload)) return null;
  return (
    <button
      className="icon-button"
      aria-label={`Baixar planilha do upload ${upload.id} (reconstruída dos dados enviados)`}
      title="Pede a planilha dos DADOS enviados, reconstruídos — não o arquivo original byte a byte. Ela é gerada em segundo plano e fica em Downloads."
      disabled={downloading}
      onClick={event => {
        event.stopPropagation();
        onDownload();
      }}
    >
      {downloading ? <LoaderCircle className="spin" size={16}/> : <Download size={16}/>}
    </button>
  );
}

function OpenButton({ upload, uploadHref }: Pick<UploadItemProps, "upload" | "uploadHref">) {
  // Controle REAL (teclado e leitor de tela não têm como clicar na linha
  // nem no card), além de ser a dica visível de que eles abrem algo. É um
  // link, então também abre em nova aba; para a propagação pra o clique
  // modificado não navegar a aba atual pelo `onClick` da linha/card.
  return (
    <AppLink
      className="upload-card-open"
      aria-label={`Abrir detalhes do upload ${upload.id}`}
      to={uploadHref}
      stopPropagation
    >
      Abrir detalhes<ChevronRight size={13}/>
    </AppLink>
  );
}

function UploadName({ upload, active, tone, size }: { upload: UploadSummary; active: boolean; tone: string; size: number }) {
  return (
    <>
      <span className={`upload-file-icon ${tone}`}>
        {active ? <LoaderCircle className="spin" size={size}/> : <FileSpreadsheet size={size}/>}
      </span>
      <span className="upload-card-name">
        <strong title={uploadLabel(upload)}>{uploadLabel(upload)}</strong>
        {upload.filename && (
          <small>#{upload.id} · {upload.clipping_service ?? "sem modelo"}</small>
        )}
      </span>
    </>
  );
}

function runLabel(upload: UploadSummary, now: number): string | null {
  const timing = uploadTiming(upload, now);
  if (timing.runSeconds === null) return null;
  return `${formatDuration(timing.runSeconds)}${timing.running ? " (rodando)" : ""}`;
}

function UploadRow({ upload, now, downloading, onOpen, uploadHref, mentionsHref, onDownload }: UploadItemProps) {
  const meta = statusMeta(upload.status, upload.is_advancing);
  const active = ACTIVE_STATUSES.has(upload.status);
  return (
    <tr className="upload-row" onClick={onOpen}>
      <td>
        <span className="upload-row-file">
          <UploadName upload={upload} active={active} tone={meta.tone} size={16} />
        </span>
      </td>
      <td>
        <span className="upload-row-status">
          <span className={`upload-status ${meta.tone}`}>{meta.label}</span>
          {active && (
            <span className="upload-progress-track">
              <span
                className={`upload-progress-fill ${meta.tone}`}
                style={{ width: `${progressPercent(upload)}%` }}
              />
            </span>
          )}
        </span>
        <UploadIssuesLine upload={upload} />
      </td>
      <td className="is-number">
        <span className="upload-progress-meta">
          {upload.processed_rows}/{upload.total_rows ?? "?"} linhas
        </span>
      </td>
      <td className="is-number">
        <span className="upload-progress-meta">{runLabel(upload, now) ?? "—"}</span>
      </td>
      <td>
        <MentionsLink upload={upload} mentionsHref={mentionsHref} />
      </td>
      <td>
        <span className="upload-row-actions">
          <DownloadButton upload={upload} downloading={downloading} onDownload={onDownload} />
          <OpenButton upload={upload} uploadHref={uploadHref} />
        </span>
      </td>
    </tr>
  );
}

function UploadCard({ upload, now, downloading, onOpen, uploadHref, mentionsHref, onDownload }: UploadItemProps) {
  const meta = statusMeta(upload.status, upload.is_advancing);
  const active = ACTIVE_STATUSES.has(upload.status);
  const run = runLabel(upload, now);
  return (
    // Clique no CARD abre o detalhe. É um `onClick` no `<article>` e não o
    // truque de stretched-link: o `button:active { transform }` global cria
    // bloco de contenção, e no mousedown a área clicável encolhia.
    <article className="upload-card" onClick={onOpen}>
      {/* Nome em linha própria: dividindo a linha com a tag de status e o
          download, "linear" virava "li…". */}
      <div className="upload-card-head">
        <UploadName upload={upload} active={active} tone={meta.tone} size={17} />
        <DownloadButton upload={upload} downloading={downloading} onDownload={onDownload} />
      </div>
      <span className="upload-progress-track">
        <span
          className={`upload-progress-fill ${meta.tone}`}
          style={{ width: `${progressPercent(upload)}%` }}
        />
      </span>
      <div className="upload-card-meta-row">
        <span className={`upload-status ${meta.tone}`}>{meta.label}</span>
        <span className="upload-progress-meta">
          {upload.processed_rows}/{upload.total_rows ?? "?"} linhas
          {run !== null && ` · ${run}`}
        </span>
      </div>
      <UploadIssuesLine upload={upload} />
      <div className="upload-card-foot-row">
        <MentionsLink upload={upload} mentionsHref={mentionsHref} />
        <OpenButton upload={upload} uploadHref={uploadHref} />
      </div>
    </article>
  );
}
